import { get } from './_core.js';

export interface ProviderHealthRow {
  provider: string;
  model: string | null;
  kind: string;
  calls: number;
  errorCount: number;
  errorRate: number;
  p50Ms: number | null;
  p95Ms: number | null;
  totalCostNano: string;
  topErrorKind: string | null;
}

export interface ProviderHealthResponse {
  rows: ProviderHealthRow[];
  since: string;
}

export async function fetchProvidersHealth(): Promise<ProviderHealthResponse> {
  const res = await get<ProviderHealthResponse>('/admin/providers/health');
  return res.data;
}

// ─── BO-5 A.1 — Provider latency-history ─────────────────────────────────

export interface LatencyBucket {
  hour: string;
  p50Ms: number | null;
  p95Ms: number | null;
  p99Ms: number | null;
  callCount: number;
  errorCount: number;
}

export interface LatencyHistoryResponse {
  range: string;
  buckets: LatencyBucket[];
}

export async function fetchProviderLatencyHistory(
  provider: string,
  model: string,
  range = '24h',
): Promise<LatencyHistoryResponse> {
  const res = await get<LatencyHistoryResponse>(
    `/admin/providers/${encodeURIComponent(provider)}/${encodeURIComponent(model)}/latency-history?range=${encodeURIComponent(range)}`,
  );
  return res.data;
}

// ─── BO-5 A.2 — Provider error-breakdown ─────────────────────────────────

export interface ErrorBreakdownRow {
  errorKind: string | null;
  count: number;
  lastSeen: string;
  sampleSpanIds: string[];
}

export interface ErrorBreakdownResponse {
  range: string;
  errors: ErrorBreakdownRow[];
}

export async function fetchProviderErrorBreakdown(
  provider: string,
  model: string,
  range = '24h',
): Promise<ErrorBreakdownResponse> {
  const res = await get<ErrorBreakdownResponse>(
    `/admin/providers/${encodeURIComponent(provider)}/${encodeURIComponent(model)}/error-breakdown?range=${encodeURIComponent(range)}`,
  );
  return res.data;
}

// ─── BO-5 A.3 — Provider recent-calls ────────────────────────────────────

export interface RecentCallRow {
  spanId: string;
  startedAt: string;
  durationMs: number;
  isError: boolean;
  userEmail: string | null;
  sessionId: string | null;
  costNano: string | null;
  /** Raw provider cost (sin markup). Preferido en vistas operativas. */
  providerCostNano?: string | null;
  markupBps?: number | null;
}

export async function fetchProviderRecentCalls(
  provider: string,
  model: string,
  limit = 10,
): Promise<{ calls: RecentCallRow[] }> {
  const res = await get<{ calls: RecentCallRow[] }>(
    `/admin/providers/${encodeURIComponent(provider)}/${encodeURIComponent(model)}/recent-calls?limit=${limit}`,
  );
  return res.data;
}
